/**
 * Checkpoint history as an inline SVG sparkline: the hidden rating at the start
 * and after each checkpoint, with faint level-band lines behind it. The
 * geometry comes from screens/model/sparkline.ts; this only draws it.
 */
import { useId } from 'react';
import {
  buildSparkline,
  levelGridLines,
  sparklineRange,
  type SparklineBox,
} from '../screens/model/sparkline.ts';
import './ui.css';

export interface SparklineProps {
  values: readonly number[];
  /** Accessible summary, e.g. "Level 3, then 4, then 6". */
  label: string;
  width?: number;
  height?: number;
}

export function Sparkline({ values, label, width = 280, height = 76 }: SparklineProps) {
  const gradientId = useId();
  const box: SparklineBox = { width, height, padding: 7 };
  const range = sparklineRange(values);
  const geometry = buildSparkline(values, box, range);
  if (!geometry) return null;
  const last = geometry.points[geometry.points.length - 1];

  return (
    <svg
      className="ui-sparkline"
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={label}
      focusable="false"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" style={{ stopColor: 'var(--ds-accent)', stopOpacity: 0.45 }} />
          <stop offset="1" style={{ stopColor: 'var(--ds-accent)', stopOpacity: 0 }} />
        </linearGradient>
      </defs>
      {levelGridLines(range, box).map((line) => (
        <line
          key={line.level}
          className="ui-sparkline__grid"
          x1={box.padding}
          x2={width - box.padding}
          y1={line.y}
          y2={line.y}
          vectorEffect="non-scaling-stroke"
        />
      ))}
      <path d={geometry.area} fill={`url(#${gradientId})`} stroke="none" />
      <path className="ui-sparkline__line" d={geometry.line} vectorEffect="non-scaling-stroke" />
      {geometry.points.map((p, i) => (
        <circle
          key={i}
          className={p === last ? 'ui-sparkline__dot ui-sparkline__dot--last' : 'ui-sparkline__dot'}
          cx={p.x}
          cy={p.y}
          r={p === last ? 5 : 3.2}
        />
      ))}
    </svg>
  );
}
